import { queryOptions, useMutation, usePrefetchQuery, useQueryClient } from '@tanstack/react-query';

import { userMetadataApi } from '@/shared/api';
import type { UserMetadata } from '@/shared/api';
import { userMetadataKeys } from '@/shared/lib/queryKeys';
import { mergeClientSort } from '../model/clientSort';
import type { ClientSortChange } from '../model/clientSort';

export const userMetadataQueryOptions = () =>
  queryOptions({
    queryKey: userMetadataKeys.all,
    queryFn: ({ signal, client, queryKey }) =>
      userMetadataApi.get({ signal }).catch((error: unknown): UserMetadata => {
        if (client.getQueryData(queryKey) !== undefined) throw error;
        return {};
      }),
    meta: { errorToast: false },
  });

export const usePrefetchClientSort = () => usePrefetchQuery(userMetadataQueryOptions());

export const useSaveClientSort = () => {
  const queryClient = useQueryClient();
  const { queryKey } = userMetadataQueryOptions();

  return useMutation({
    mutationKey: userMetadataKeys.all,
    scope: { id: 'user-metadata' },
    mutationFn: async (change: ClientSortChange) =>
      userMetadataApi.set(mergeClientSort(await userMetadataApi.get(), change)),
    onMutate: async (change) => {
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, (metadata = {}) => mergeClientSort(metadata, change));
    },
    onSettled: async () => {
      if (queryClient.isMutating({ mutationKey: userMetadataKeys.all }) === 1) {
        await queryClient.invalidateQueries({ queryKey });
      }
    },
    meta: { errorToast: 'Failed to save sorting. Contact support.' },
  });
};
